<template>
  <div class="flex flex-col min-h-0 flex-1">
    <div
      class="flex flex-col gap-4 overflow-y-auto custom-scroll px-4 pt-1 pb-3 flex-1"
    >
      <div v-if="initiatedAt" class="flex flex-col gap-1">
        <span class="text-xs font-semibold text-gray-500">
          {{ $t('CONTACT_PANEL.INITIATED_AT') }}
        </span>
        <span class="text-sm text-gray-700">{{ initiatedAt.timestamp }}</span>
      </div>
      <div v-if="browserLanguage" class="flex flex-col gap-1">
        <span class="text-xs font-semibold text-gray-500">
          {{ $t('CONTACT_PANEL.BROWSER_LANGUAGE') }}
        </span>
        <span class="text-sm text-gray-700">{{ browserLanguage }}</span>
      </div>
      <div v-if="referer" class="flex flex-col gap-1">
        <span class="text-xs font-semibold text-gray-500">
          {{ $t('CONTACT_PANEL.INITIATED_FROM') }}
        </span>
        <a
          :href="referer"
          target="_blank"
          rel="noopener noreferrer nofollow"
          class="text-sm text-blue-400 truncate block"
        >
          {{ referer }}
        </a>
      </div>
      <div v-if="browserName" class="flex flex-col gap-1">
        <span class="text-xs font-semibold text-gray-500">
          {{ $t('CONTACT_PANEL.BROWSER') }}
        </span>
        <span class="text-sm text-gray-700">{{ browserName }}</span>
      </div>
      <div v-if="platformName" class="flex flex-col gap-1">
        <span class="text-xs font-semibold text-gray-500">
          {{ $t('CONTACT_PANEL.OS') }}
        </span>
        <span class="text-sm text-gray-700">{{ platformName }}</span>
      </div>
      <div v-if="ipAddress" class="flex flex-col gap-1">
        <span class="text-xs font-semibold text-gray-500">
          {{ $t('CONTACT_PANEL.IP_ADDRESS') }}
        </span>
        <span class="text-sm text-gray-700">{{ ipAddress }}</span>
      </div>
      <CustomFieldList
        attribute-type="conversation_attribute"
        attribute-class="conversation--attribute"
      />
    </div>

    <div
      class="flex items-center justify-center pt-4 shrink-0"
      style="box-shadow: rgb(239 239 239) 1px -2px 3px 0px"
    >
      <button
        class="flex items-center gap-1 h-8 px-3 border border-solid border-green-500 text-green-500 hover:bg-green-50 text-sm rounded-[5px] transition-colors"
        @click="openAddCustomFieldModal"
      >
        <span class="material-symbols-outlined text-[16px]">add</span>
        {{ $t('CUSTOM_ATTRIBUTES.ADD_BUTTON_TEXT') }}
      </button>
    </div>

    <custom-field-modal
      :show="showAddCustomFieldsModal"
      initial-attribute-type="conversation_attribute"
      @close="closeAddCustomFieldModal"
      @add-field="addAttribute"
    />
  </div>
</template>

<script>
import CustomFieldList from './CustomFieldList.vue';
import CustomFieldModal from './CustomFieldModal.vue';
import { getLanguageName } from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import customFieldMixins from './customFieldMixins';

export default {
  name: 'ConversationTab',
  components: { CustomFieldList, CustomFieldModal },
  mixins: [customFieldMixins],
  computed: {
    conversationAdditionalAttributes() {
      const meta = this.$store.getters[
        'conversationMetadata/getConversationMetadata'
      ](this.currentChat.id);
      return (meta && meta.additional_attributes) || {};
    },
    initiatedAt() {
      return this.conversationAdditionalAttributes.initiated_at;
    },
    referer() {
      return this.conversationAdditionalAttributes.referer;
    },
    browserName() {
      const { browser } = this.conversationAdditionalAttributes;
      if (!browser) return '';
      const { browser_name: name = '', browser_version: version = '' } = browser;
      return `${name} ${version}`.trim();
    },
    browserLanguage() {
      return getLanguageName(
        this.conversationAdditionalAttributes.browser_language
      );
    },
    platformName() {
      const { browser } = this.conversationAdditionalAttributes;
      if (!browser) return '';
      const { platform_name: name = '', platform_version: version = '' } =
        browser;
      return `${name} ${version}`.trim();
    },
    ipAddress() {
      return (this.contact.additional_attributes || {}).created_at_ip;
    },
    attributeType() {
      return 'conversation_attribute';
    },
  },
  methods: {
    async addAttribute({ attribute, attributeType }) {
      try {
        if (attributeType !== 'conversation_attribute') return;

        const {
          attribute_key: attributeKey,
          attribute_display_type: attributeDisplayType,
          default_value: attributeDefaultValue,
        } = attribute;
        const isCheckbox = attributeDisplayType === 'checkbox';
        const defaultValue = isCheckbox ? false : attributeDefaultValue || null;

        await this.$store.dispatch('updateCustomAttributes', {
          conversationId: this.currentChat.id,
          customAttributes: {
            ...this.customAttributes,
            [attributeKey]: defaultValue,
          },
        });

        bus.$emit(BUS_EVENTS.FOCUS_CUSTOM_ATTRIBUTE, attributeKey);
        this.showAlert(this.$t('CUSTOM_ATTRIBUTES.FORM.ADD.SUCCESS'));
      } catch (error) {
        const errorMessage =
          error?.response?.message ||
          this.$t('CUSTOM_ATTRIBUTES.FORM.ADD.ERROR');
        this.showAlert(errorMessage);
      } finally {
        this.closeAddCustomFieldModal();
      }
    },
  },
};
</script>
